import {
  isLikelyStrapiDocumentId,
  parseStoredParameterUjiDocumentIds,
  type StrapiParameterUjiBakuMutuGridRow,
  type StrapiSampleInformationRow,
} from '../api/strapiCrm';
import type { ChemParameterUjiLookupRow } from '../components/ChemParameterUjiCell';
import { formatChemicalNotation, formatParameterNameWithSymbol } from '../chemistry/latexChemDisplay';

/** Token terpilih = `parameter-uji` documentId (bukan id BPU). */
export function selectedParameterUjiDocumentIdsFromGridValue(
  rows: StrapiParameterUjiBakuMutuGridRow[],
  value: string,
): Set<string> {
  const tokens = parseStoredParameterUjiDocumentIds(value);
  const ids = new Set<string>();
  if (!tokens.length) return ids;
  if (tokens.every((t) => isLikelyStrapiDocumentId(t))) {
    for (const t of tokens) ids.add(t.trim());
    return ids;
  }
  const want = new Set(tokens.map((t) => t.toLowerCase()));
  for (const r of rows) {
    const n = (r.nama_parameter ?? '').trim().toLowerCase();
    if (n && want.has(n)) ids.add(r.parameter_uji_document_id.trim());
  }
  return ids;
}

/** Ringkasan nama + simbol (notasi kimia) dari baris grid + nilai tersimpan. */
export function formatParameterSelectionSummaryFromGridRows(
  rows: StrapiParameterUjiBakuMutuGridRow[],
  storedValue: string,
): string {
  const sel = selectedParameterUjiDocumentIdsFromGridValue(rows, storedValue);
  return rows
    .filter((r) => sel.has(r.parameter_uji_document_id.trim()))
    .map((r) => formatParameterNameWithSymbol(r.nama_parameter, r.simbol))
    .filter(Boolean)
    .join(' · ');
}

function formatParameterUjiLookupPlain(row: ChemParameterUjiLookupRow, documentId?: string): string {
  const n = (row.nama_parameter ?? '').trim();
  const sym = (row.simbol ?? '').trim();
  if (n && sym) return `${formatChemicalNotation(n)} (${formatChemicalNotation(sym)})`;
  if (n) return formatChemicalNotation(n);
  if (sym) return formatChemicalNotation(sym);
  const id = (documentId ?? '').trim();
  return id.length > 10 ? `${id.slice(0, 10)}…` : id || '';
}

/** Plain text untuk sort/export: semua parameter dipisah koma. */
export function formatSampleParameterUjiPlain(
  raw: string | null | undefined,
  opts?: {
    items?: StrapiSampleInformationRow['parameter_ujis'];
    puLookup?: ReadonlyMap<string, ChemParameterUjiLookupRow>;
    separator?: string;
  },
): string {
  const sep = opts?.separator ?? ', ';
  if (opts?.items?.length) {
    return opts.items
      .map((pu) => {
        const key = pu.documentId.trim();
        const fromLookup = key ? opts.puLookup?.get(key) : undefined;
        return formatParameterUjiLookupPlain(fromLookup ?? pu, key);
      })
      .filter(Boolean)
      .join(sep);
  }
  if (raw == null || typeof raw !== 'string') return '';
  const s = raw.trim();
  if (!s) return '';
  const tokens = parseStoredParameterUjiDocumentIds(s);
  if (!tokens.length) return '';
  if (tokens.every((t) => isLikelyStrapiDocumentId(t))) {
    return tokens
      .map((t) => {
        const key = t.trim();
        const row = opts?.puLookup?.get(key);
        return row ? formatParameterUjiLookupPlain(row, key) : `${key.slice(0, 10)}…`;
      })
      .filter(Boolean)
      .join(sep);
  }
  return tokens.map((t) => formatChemicalNotation(t)).join(sep);
}
